/**
 * Client-side half of the 2-month password rotation (backend:
 * password_policy.py + dashboard_access_middleware.py).
 *
 * The backend lets an expired account sign in — otherwise there would be no
 * session to change the password with — but answers every other endpoint with
 * 403 PASSWORD_EXPIRED. This module holds the "must rotate" flag so both entry
 * points agree on it:
 *
 *   - sign-in, which reports the state up front (authController)
 *   - any later 403 PASSWORD_EXPIRED, for a session that goes stale while open
 *
 * Deliberately imports nothing: utils/api.js reads it from its interceptor, so
 * pulling axios in here would close an import cycle.
 */

export const PASSWORD_EXPIRED_MESSAGE =
  "Your password has expired. Please set a new password to continue.";

const EXPIRED_KEY = "password_expired";
const EXPIRES_AT_KEY = "password_expires_at";

export const PASSWORD_EXPIRY_EVENT = "password-expiry-change";

/** Flag the session as needing a new password (or clear it after one is set). */
export const setPasswordExpired = (expired, expiresAt = null) => {
  if (expired) {
    localStorage.setItem(EXPIRED_KEY, "true");
  } else {
    localStorage.removeItem(EXPIRED_KEY);
  }

  if (expiresAt) {
    localStorage.setItem(EXPIRES_AT_KEY, expiresAt);
  } else if (!expired) {
    localStorage.removeItem(EXPIRES_AT_KEY);
  }

  // PrivateRoute is already mounted when a mid-session 403 arrives, so it has
  // to be told rather than re-reading storage on its own.
  window.dispatchEvent(
    new CustomEvent(PASSWORD_EXPIRY_EVENT, { detail: { expired: !!expired } }),
  );
};

export const isPasswordExpired = () =>
  localStorage.getItem(EXPIRED_KEY) === "true";

export const getPasswordExpiresAt = () =>
  localStorage.getItem(EXPIRES_AT_KEY) || null;

export const clearPasswordExpiry = () => {
  localStorage.removeItem(EXPIRED_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
};

/** True when a failed request was refused specifically for a stale password. */
export const isPasswordExpiredError = (error) => {
  const response = error?.response;
  if (!response || response.status !== 403) return false;
  const data = response.data || {};
  return data.password_expired === true || data?.error?.code === "PASSWORD_EXPIRED";
};
